import { Injectable, signal } from '@angular/core';

export interface Alert {
  id: string;
  type: 'info' | 'success' | 'warning' | 'error';
  title: string;
  message: string;
  dismissible: boolean;
  autoClose?: boolean;
  autoCloseDelay?: number;
  category?: 'scorecard' | 'tournament' | 'round' | 'general';
}

@Injectable({
  providedIn: 'root'
})
export class AlertService {
  private _alerts = signal<Alert[]>([]);
  private _alertCounter = 0;
  private readonly MAX_ALERTS = 50; // Maximum number of alerts to keep

  // Public readonly signals
  readonly alerts = this._alerts.asReadonly();

  /**
   * Show an info alert
   */
  info(title: string, message: string, options?: Partial<Pick<Alert, 'dismissible' | 'autoClose' | 'autoCloseDelay' | 'category'>>): string {
    return this.addAlert('info', title, message, options);
  }

  /**
   * Show a success alert
   */
  success(title: string, message: string, options?: Partial<Pick<Alert, 'dismissible' | 'autoClose' | 'autoCloseDelay' | 'category'>>): string {
    return this.addAlert('success', title, message, options);
  }

  /**
   * Show a warning alert
   */
  warning(title: string, message: string, options?: Partial<Pick<Alert, 'dismissible' | 'autoClose' | 'autoCloseDelay' | 'category'>>): string {
    return this.addAlert('warning', title, message, options);
  }

  /**
   * Show an error alert
   */
  error(title: string, message: string, options?: Partial<Pick<Alert, 'dismissible' | 'autoClose' | 'autoCloseDelay' | 'category'>>): string {
    return this.addAlert('error', title, message, options);
  }

  /**
   * Dismiss a specific alert by ID
   */
  dismiss(alertId: string): void {
    const currentAlerts = this._alerts();
    const updatedAlerts = currentAlerts.filter(alert => alert.id !== alertId);
    this._alerts.set(updatedAlerts);
  }

  /**
   * Dismiss all alerts
   */
  dismissAll(): void {
    this._alerts.set([]);
  }

  /**
   * Get a specific alert by ID
   */
  getAlert(alertId: string): Alert | undefined {
    return this._alerts().find(alert => alert.id === alertId);
  }

  /**
   * Check if there are any alerts
   */
  hasAlerts(): boolean {
    return this._alerts().length > 0;
  }

  /**
   * Get alerts count
   */
  getAlertsCount(): number {
    return this._alerts().length;
  }

  /**
   * Check if there are any scorecard alerts
   */
  hasScorecardAlerts(): boolean {
    return this._alerts().some(alert => alert.category === 'scorecard');
  }

  /**
   * Get the count of scorecard alerts
   */
  getScorecardAlertsCount(): number {
    return this._alerts().filter(alert => alert.category === 'scorecard').length;
  }

  /**
   * Get an appropriate title based on alert categories
   */
  getContainerTitle(): string {
    const alerts = this._alerts();
    const scorecardCount = alerts.filter(alert => alert.category === 'scorecard').length;
    const totalCount = alerts.length;

    if (scorecardCount === totalCount && scorecardCount > 0) {
      return `Scorecards Updated (${scorecardCount})`;
    } else if (scorecardCount > 0) {
      return `Notifications (${totalCount})`;
    } else {
      return `Notifications (${totalCount})`;
    }
  }

  /**
   * Dismiss oldest alerts if we exceed the maximum
   */
  private trimAlerts(): void {
    const currentAlerts = this._alerts();
    if (currentAlerts.length > this.MAX_ALERTS) {
      const trimmedAlerts = currentAlerts.slice(0, this.MAX_ALERTS); // Keep the first N alerts (newest)
      this._alerts.set(trimmedAlerts);
    }
  }

  private addAlert(
    type: Alert['type'],
    title: string,
    message: string,
    options?: Partial<Pick<Alert, 'dismissible' | 'autoClose' | 'autoCloseDelay' | 'category'>>
  ): string {
    const alertId = `alert-${++this._alertCounter}`;

    const alert: Alert = {
      id: alertId,
      type,
      title,
      message,
      dismissible: options?.dismissible ?? true,
      autoClose: options?.autoClose ?? false,
      autoCloseDelay: options?.autoCloseDelay ?? 5000,
      category: options?.category ?? 'general'
    };

    const currentAlerts = this._alerts();
    this._alerts.set([alert, ...currentAlerts]); // Add new alert at the beginning

    // Trim alerts if we have too many
    this.trimAlerts();

    // Auto-close if configured
    if (alert.autoClose && alert.autoCloseDelay) {
      setTimeout(() => {
        this.dismiss(alertId);
      }, alert.autoCloseDelay);
    }

    return alertId;
  }
}
